// components/InformacionAdicional.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField
} from '@mui/material';

/**
 * Este componente muestra/edita:
 * - URL Estadística
 */

function InformacionAdicional({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  handleChange
}) {
  const { t } = useTranslation();

  // === Render para MOBILE ===
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper'
          }}
        >
          6. {t('createPlayer.sections.additionalInfo')}
        </Typography>

        <Box sx={{ p: 2 }}>
          {isEditing ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.statsUrl')}
                  name="url"
                  value={editablePlayer.url || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.instatUrl')}
                  name="instat_url"
                  value={editablePlayer.instat_url || ''}
                  onChange={handleChange}
                  fullWidth
                  helperText={t('createPlayer.fields.instatUrlHelper')}
                />
              </Grid>
            </Grid>
          ) : (
            // READ ONLY (MOBILE)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!!player.url && (
                <Typography>
                  <strong>{t('createPlayer.fields.statsUrl')}:</strong>{' '}
                  <a href={player.url} target="_blank" rel="noopener noreferrer">
                    {player.url}
                  </a>
                </Typography>
              )}
              {!!player.instat_url && (
                <Typography>
                  <strong>{t('createPlayer.fields.instatUrl')}:</strong>{' '}
                  <a href={player.instat_url} target="_blank" rel="noopener noreferrer">
                    {player.instat_url}
                  </a>
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // === Render para DESKTOP ===
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        6. {t('createPlayer.sections.additionalInfo')}
      </Typography>

      {isEditing ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('createPlayer.fields.statsUrl')}
              name="url"
              value={editablePlayer.url || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('createPlayer.fields.instatUrl')}
              name="instat_url"
              value={editablePlayer.instat_url || ''}
              onChange={handleChange}
              fullWidth
              helperText={t('createPlayer.fields.instatUrlHelper')}
            />
          </Grid>
        </Grid>
      ) : (
        // READ ONLY (DESKTOP)
        <Box sx={{ mt: 0.5 }}>
          <Grid container rowSpacing={1} columnSpacing={2.5}>
            {!!player.url && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.statsUrl')}:
                  </Typography>
                  <Typography
                    component="a"
                    href={player.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: '0.95rem',
                      color: 'primary.main',
                      fontWeight: 400,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {player.url}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.instat_url && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.instatUrl')}:
                  </Typography>
                  <Typography
                    component="a"
                    href={player.instat_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: '0.95rem',
                      color: 'primary.main',
                      fontWeight: 400,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {player.instat_url}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default InformacionAdicional;
